import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import SectionHeader from "./SectionHeader";
import Reveal from "./Reveal";

export default function Stats() {
  const { t } = useLanguage();
  const s = t.stats;
  const [tab, setTab] = useState("liga");
  const active = tab === "liga" ? s.liga : s.copa;

  return (
    <section id="estadisticas" className="border-y border-white/5 bg-[#0A0D14]" data-testid="estadisticas-section">
      <div className="mx-auto max-w-7xl scroll-mt-20 px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
        <SectionHeader number={`${s.number} · ${t.nav.estadisticas}`} title={s.title}>
          {s.disclaimer}
        </SectionHeader>

        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="stats-cards-grid">
          {s.cards.map((c, i) => (
            <Reveal key={c.label + c.sub} delay={0.07 * i}>
              <article
                data-testid={`stat-card-${i}`}
                className="h-full rounded-xl border border-white/10 bg-[#131927]/80 p-5 transition-all duration-300 hover:border-sky-400/50 hover:shadow-[0_0_40px_rgba(56,189,248,0.08)] sm:p-6"
              >
                <p className="font-mono text-4xl font-bold tracking-tight text-white sm:text-5xl">{c.value}</p>
                <p className="mt-3 text-sm font-semibold uppercase tracking-wide text-sky-300">{c.label}</p>
                <p className="mt-1 text-xs text-slate-500">{c.sub}</p>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15} className="mt-12">
          <div className="mb-5 flex gap-2" role="tablist" data-testid="stats-tabs">
            {["liga", "copa"].map((k) => (
              <button
                key={k}
                role="tab"
                aria-selected={tab === k}
                data-testid={`stats-tab-${k}`}
                onClick={() => setTab(k)}
                className={`rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-[0.14em] transition-colors duration-300 ${
                  tab === k
                    ? "bg-sky-400 text-[#07090E]"
                    : "border border-white/15 text-slate-400 hover:border-sky-400/50 hover:text-sky-300"
                }`}
              >
                {s.tabs[k]}
              </button>
            ))}
          </div>

          <div className="overflow-x-auto rounded-xl border border-white/10" data-testid={`stats-table-${tab}`}>
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead>
                <tr className="border-b border-white/10 bg-[#131927]">
                  {s.tableCols.map((col) => (
                    <th key={col} className="px-4 py-3.5 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-white/5 bg-[#0E131F]">
                  {active.row.map((cell, i) => (
                    <td key={i} className={`px-4 py-4 ${i === 0 ? "font-semibold text-slate-100" : "font-mono text-slate-300"}`}>
                      {cell}
                    </td>
                  ))}
                </tr>
                <tr className="bg-[#131927]/60" data-testid="stats-total-row">
                  {s.totalRow.map((cell, i) => (
                    <td key={i} className={`px-4 py-4 ${i === 0 ? "font-semibold text-sky-300" : "font-mono text-sky-200"}`}>
                      {cell}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
            <span className="font-semibold text-slate-400">{s.source}</span>
            <span>{s.cutoff}</span>
          </div>
          <p className="mt-2 text-xs text-slate-600">{s.totalFootnote}</p>
          {active.footnote && <p className="mt-1 text-xs text-slate-500">{active.footnote}</p>}

          {tab === "copa" && (
            <div className="mt-8">
              <h4 className="mb-3 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-sky-400">
                {s.recentTitle}
              </h4>
              <div className="overflow-x-auto rounded-xl border border-white/10" data-testid="stats-recent-table">
                <table className="w-full min-w-[560px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-white/10 bg-[#131927]">
                      {s.recentCols.map((col) => (
                        <th key={col} className="px-4 py-3 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {s.recent.map((m) => (
                      <tr key={m.date} className="border-b border-white/5 bg-[#0E131F] last:border-0">
                        <td className="px-4 py-3 font-mono text-slate-400">{m.date}</td>
                        <td className="px-4 py-3 font-semibold text-slate-100">{m.match}</td>
                        <td className="px-4 py-3 font-mono text-sky-300">{m.assist}</td>
                        <td className="px-4 py-3 font-mono text-slate-300">{m.rating}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Reveal>
      </div>
    </section>
  );
}
